import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Newspaper, 
  BookOpen, 
  Users, 
  LogOut,
  Plus,
  ChevronRight,
  BarChart3,
  Video,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { admin, token, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentNews, setRecentNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, newsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/news?published_only=false&limit=5`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (newsRes.ok) {
        setRecentNews(await newsRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { 
      icon: LayoutDashboard, 
      label: 'Tableau de bord', 
      path: '/admin',
      active: true
    },
    { 
      icon: Newspaper, 
      label: 'Actualités', 
      path: '/admin/news',
      badge: recentNews.length
    },
    { 
      icon: BookOpen, 
      label: 'Livres', 
      path: '/admin/books',
      badge: stats?.total_books || 0
    },
    { 
      icon: Video, 
      label: 'Interviews', 
      path: '/admin/videos'
    },
    { 
      icon: FileText, 
      label: 'Soumissions', 
      path: '/admin/submissions'
    },
    { 
      icon: Users, 
      label: 'Utilisateurs', 
      path: '/admin/users'
    }
  ];

  return (
    <div className="min-h-screen bg-stone-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-900 text-white flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <h1 className="text-xl font-serif font-bold">Quid Novi</h1>
          <p className="text-stone-400 text-sm mt-1">Administration</p>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                    item.active 
                      ? 'bg-hermes-600 text-white' 
                      : 'text-stone-300 hover:bg-stone-800'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {item.badge !== undefined && (
                    <span className="ml-auto text-xs bg-stone-700 px-2 py-0.5 rounded">
                      {item.badge}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-stone-700">
          <div className="flex items-center gap-3 px-4 py-2 text-stone-300">
            <Users className="w-5 h-5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{admin?.full_name || admin?.email}</p>
              <p className="text-xs text-stone-500">Administrateur</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 mt-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="mb-8">
          <h2 className="text-2xl font-serif font-bold text-stone-900">
            Bonjour, {admin?.full_name || 'Administrateur'}
          </h2>
          <p className="text-stone-600 mt-1">Bienvenue dans votre espace d'administration</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 shadow-sm border-l-4 border-hermes-600">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-600 text-sm">Actualités</p>
                <p className="text-3xl font-bold text-stone-900 mt-1">{recentNews.length}</p>
              </div>
              <Newspaper className="w-10 h-10 text-hermes-200" />
            </div>
          </div>

          <div className="bg-white p-6 shadow-sm border-l-4 border-hermes-600">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-600 text-sm">Livres publiés</p>
                <p className="text-3xl font-bold text-stone-900 mt-1">{stats?.published_books || 0}</p>
              </div>
              <BookOpen className="w-10 h-10 text-hermes-200" />
            </div>
          </div>

          <div className="bg-white p-6 shadow-sm border-l-4 border-hermes-600">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-600 text-sm">Administrateurs</p>
                <p className="text-3xl font-bold text-stone-900 mt-1">{stats?.total_admins || 1}</p>
              </div>
              <Users className="w-10 h-10 text-hermes-200" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Link
            to="/admin/news/new"
            className="bg-white p-6 shadow-sm hover:shadow-md transition-shadow group"
            data-testid="quick-add-news"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-hermes-100 flex items-center justify-center rounded">
                <Plus className="w-6 h-6 text-hermes-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-stone-900">Nouvelle actualité</h3>
                <p className="text-sm text-stone-600">Ajouter une nouvelle</p>
              </div>
              <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-hermes-600 transition-colors" />
            </div>
          </Link>

          <Link
            to="/admin/books/new"
            className="bg-white p-6 shadow-sm hover:shadow-md transition-shadow group"
            data-testid="quick-add-book"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-hermes-100 flex items-center justify-center rounded">
                <Plus className="w-6 h-6 text-hermes-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-stone-900">Nouveau livre</h3>
                <p className="text-sm text-stone-600">Ajouter une fiche livre</p>
              </div>
              <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-hermes-600 transition-colors" />
            </div>
          </Link>
        </div>

        {/* Recent News */}
        <div className="bg-white shadow-sm">
          <div className="p-6 border-b border-stone-200 flex items-center justify-between">
            <h3 className="font-medium text-stone-900">Dernières actualités</h3>
            <Link 
              to="/admin/news" 
              className="text-sm text-hermes-600 hover:text-hermes-700"
            >
              Voir tout
            </Link>
          </div>
          <div className="divide-y divide-stone-100">
            {recentNews.length === 0 ? (
              <div className="p-6 text-center text-stone-500">
                Aucune actualité pour le moment
              </div>
            ) : (
              recentNews.map((news) => (
                <Link
                  key={news.id}
                  to={`/admin/news/${news.id}`}
                  className="block p-4 hover:bg-stone-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-stone-900">{news.title}</h4>
                      <p className="text-sm text-stone-500 mt-1">
                        {news.category} • {new Date(news.date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      news.published 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {news.published ? 'Publié' : 'Brouillon'}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
